// SKYCORE MAP.NAV.1 — modèle de navigation cartographique PUR (aucun DOM/Leaflet), réutilisable par
// toutes les cartes Web et, plus tard, par WEBXR.8. Lecture seule : aucune commande drone.
// Distinction stricte : headingDeg = avant physique du drone (cap Pixhawk) ; courseDeg = direction
// RÉELLE du déplacement au sol (COG). L'un ne remplace JAMAIS l'autre.
import { isTelemetryStale } from "@/lib/commandGating";

export const isNum = (v) => typeof v === "number" && Number.isFinite(v);

export const COURSE_SOURCE = Object.freeze({ MEASURED: "MEASURED", DERIVED: "DERIVED", UNKNOWN: "UNKNOWN" });
export const FRESHNESS = Object.freeze({ FRESH: "FRESH", STALE: "STALE", UNKNOWN: "UNKNOWN", REPLAY: "REPLAY" });

// Seuils (validés MAP.NAV.1). 40 m/s = filtre de PLAUSIBILITÉ des données, pas une vitesse autorisée.
export const COURSE_RULES = Object.freeze({
  minDistM: 3, minDtS: 1, maxDtS: 10, minSpeedMps: 0.7, maxSpeedMps: 40,
  maxSegmentDeltaDeg: 45, stationarySpeedMps: 0.5, accuracyFactor: 2, maxSampleAgeS: 10,
});

// Latitude/longitude exploitables. 0 est une coordonnée valide (jamais exclue par un test "truthy").
export function isValidLatLng(lat, lng) {
  return isNum(lat) && isNum(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

export function normalizeDeg(a) {
  if (!isNum(a)) return null;
  const n = ((a % 360) + 360) % 360;
  return n >= 360 ? 0 : n;
}

// Écart minimal signé de `from` vers `to`, dans (-180, 180].
export function angleDelta(from, to) {
  const d = ((((to - from) % 360) + 540) % 360) - 180;
  return d === -180 ? 180 : d;
}

// Angle déroulé continu (359 -> 0 = +1°) pour des transitions par le chemin le plus court.
export function unwrapDeg(prevUnwrapped, target) {
  const t = normalizeDeg(target);
  if (t == null) return isNum(prevUnwrapped) ? prevUnwrapped : 0;
  if (!isNum(prevUnwrapped)) return t;
  return prevUnwrapped + angleDelta(normalizeDeg(prevUnwrapped), t);
}

export function freshnessOf(lastSeen, nowMs = Date.now()) {
  if (!lastSeen) return FRESHNESS.UNKNOWN;
  return isTelemetryStale(lastSeen, nowMs) ? FRESHNESS.STALE : FRESHNESS.FRESH;
}

const R_EARTH = 6371000;
const rad = (d) => (d * Math.PI) / 180;
export function distanceM(a, b) {
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R_EARTH * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}
// Relèvement géographique initial a -> b (0 = Nord, 90 = Est).
export function bearingDeg(a, b) {
  const y = Math.sin(rad(b.lng - a.lng)) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lng - a.lng));
  return normalizeDeg((Math.atan2(y, x) * 180) / Math.PI);
}

// MAP.NAV.2H — état GPS CONSERVATEUR : un seul indicateur négatif suffit (contradiction -> NO_FIX).
export const GPS = Object.freeze({ FIX: "FIX", NO_FIX: "NO_FIX", UNKNOWN: "UNKNOWN" });
export function gpsStateOf(x) {
  if (!x) return GPS.UNKNOWN;
  if (x.gps_state === "no_fix" || x.gps_state === "unavailable" || x.gps_fix === false || (isNum(x.fix_type) && x.fix_type < 3)) return GPS.NO_FIX;
  return x.gps_state === "fix" || x.gps_fix === true || (isNum(x.fix_type) && x.fix_type >= 3) ? GPS.FIX : GPS.UNKNOWN;
}

// Précision horizontale en MÈTRES : h_acc_m uniquement (finie, > 0). hdop (sans unité), eph et h_acc
// (unité ambiguë) ne sont JAMAIS convertis en distance. Inconnue -> null (filtre 3 m seul).
export function horizontalAccuracyM(p) {
  const v = p ? p.h_acc_m : null;
  return isNum(v) && v > 0 ? v : null;
}

// Qualité d'une course : GPS_FIX = FIX prouvé ; GPS_UNDOCUMENTED = dérivée sans preuve GPS (anciens historiques).
export const COURSE_QUALITY = Object.freeze({ GPS_FIX: "GPS_FIX", GPS_UNDOCUMENTED: "GPS_UNDOCUMENTED" });
const GPS_VALUES = Object.values(GPS);

// Échantillon de position horodaté (t en ms, horodatage TÉLÉMÉTRIE, jamais l'heure de réception).
// Idempotent. Position globale invalide -> échantillon classé NO_FIX (coupe la trajectoire).
export function toSample(p) {
  if (!p) return null;
  const t = typeof p.t === "number" ? p.t : Date.parse(p.timestamp || p.last_seen || "");
  if (!isValidLatLng(p.lat, p.lng) || !Number.isFinite(t)) return null;
  const gpsClass = GPS_VALUES.includes(p.gpsClass) ? p.gpsClass : p.global_position_valid === false ? GPS.NO_FIX : gpsStateOf(p);
  return { lat: p.lat, lng: p.lng, t, h_acc_m: horizontalAccuracyM(p), gpsClass };
}

// Cherche, en remontant depuis `end`, le premier échantillon assez éloigné pour former un segment.
function findSegment(samples, end, rules) {
  const b = samples[end];
  for (let i = end - 1; i >= 0; i -= 1) {
    const a = samples[i];
    const dtS = (b.t - a.t) / 1000;
    if (dtS > rules.maxDtS) return { reason: "GAP_TOO_LONG" };
    const minDist = Math.max(rules.minDistM, rules.accuracyFactor * Math.max(a.h_acc_m || 0, b.h_acc_m || 0));
    const dist = distanceM(a, b);
    if (dist >= minDist && dtS >= rules.minDtS) {
      const speed = dist / dtS;
      if (speed < rules.minSpeedMps || speed > rules.maxSpeedMps) return { reason: "IMPLAUSIBLE_SPEED" };
      return { start: i, course: bearingDeg(a, b), speed };
    }
  }
  return { reason: "INSUFFICIENT_DISTANCE" };
}

// Course au sol DÉRIVÉE de l'historique GPS : ≥ 2 segments cohérents (≤ 45°), timestamps monotones,
// distance ≥ max(3 m, 2×précision), durée 1–10 s, vitesse 0,7–40 m/s. Sinon : null + raison.
// MAP.NAV.2H : seuls les échantillons POSTÉRIEURS au dernier NO_FIX sont utilisés ; qualité GPS_FIX
// uniquement si tous sont FIX, sinon GPS_UNDOCUMENTED (aucune preuve GPS enregistrée).
export function deriveCourse(rawSamples, { nowMs = null, rules = COURSE_RULES } = {}) {
  const all = (rawSamples || []).map(toSample);
  if (all.some((s) => s == null)) return { courseDeg: null, reason: "INVALID_SAMPLE" };
  for (let i = 1; i < all.length; i += 1) if (!(all[i].t > all[i - 1].t)) return { courseDeg: null, reason: "NON_MONOTONIC" };
  const lastNoFix = all.map((s) => s.gpsClass).lastIndexOf(GPS.NO_FIX);
  const samples = all.slice(lastNoFix + 1);
  if (samples.length < 3) return { courseDeg: null, reason: lastNoFix >= 0 ? "GPS_NO_FIX_IN_TRACK" : "NOT_ENOUGH_SAMPLES" };
  const last = samples[samples.length - 1];
  if (nowMs != null && (nowMs - last.t) / 1000 > rules.maxSampleAgeS) return { courseDeg: null, reason: "SAMPLES_TOO_OLD" };
  const s2 = findSegment(samples, samples.length - 1, rules);
  if (s2.reason) return { courseDeg: null, reason: s2.reason };
  const s1 = findSegment(samples, s2.start, rules);
  if (s1.reason) return { courseDeg: null, reason: s1.reason };
  if (Math.abs(angleDelta(s1.course, s2.course)) > rules.maxSegmentDeltaDeg) return { courseDeg: null, reason: "INCOHERENT_TRACK" };
  const quality = samples.every((s) => s.gpsClass === GPS.FIX) ? COURSE_QUALITY.GPS_FIX : COURSE_QUALITY.GPS_UNDOCUMENTED;
  return { courseDeg: s2.course, speedEstMps: s2.speed, quality, reason: null };
}

// MAP.NAV.2 — COG MESURÉ (Agent : EKF_VELOCITY | GPS_COG). Une valeur numérique ne suffit PAS :
// source reconnue, âge cohérent (course_age_s + (now − last_seen) ≤ 3 s ; tolérance d'horloge 1 s),
// position valide, GPS FIX, vitesse réelle connue ≥ 0,5 m/s, horodatages cohérents. Sinon : rejet.
export const MEASURED_SOURCES = Object.freeze(["EKF_VELOCITY", "GPS_COG"]);
export const MEASURED_MAX_AGE_S = 3;
export const CLOCK_SKEW_TOLERANCE_S = 1;
export function measuredCourse(x, { nowMs, replay, positionValid, gps, speedMps }) {
  const deg = normalizeDeg(x.course_over_ground_deg);
  if (deg == null || !isNum(x.course_over_ground_deg) || x.course_over_ground_deg < 0 || x.course_over_ground_deg >= 360) return { reason: "NO_MEASURED" };
  if (!MEASURED_SOURCES.includes(x.course_source)) return { reason: "UNKNOWN_SOURCE" };
  if (!positionValid) return { reason: "POSITION_INVALID" };
  if (gps !== "FIX") return { reason: "GPS_NOT_FIX" };
  if (speedMps == null || speedMps < COURSE_RULES.stationarySpeedMps) return { reason: speedMps == null ? "SPEED_UNKNOWN" : "STATIONARY" };
  if (x.course_speed_mps != null && (!isNum(x.course_speed_mps) || x.course_speed_mps < COURSE_RULES.stationarySpeedMps)) return { reason: "SOURCE_STATIONARY" };
  const age = x.course_age_s;
  if (!isNum(age) || age < 0) return { reason: "AGE_INVALID" };
  let lag = 0;
  if (!replay) {
    const seen = Date.parse(x.last_seen || "");
    if (!Number.isFinite(seen) || !isNum(nowMs)) return { reason: "LAST_SEEN_INVALID" };
    lag = (nowMs - seen) / 1000;
    if (lag < -CLOCK_SKEW_TOLERANCE_S) return { reason: "CLOCK_INCOHERENT" };
    lag = Math.max(0, lag);
    if (x.course_observed_at != null) {
      const obs = Date.parse(x.course_observed_at);
      if (!Number.isFinite(obs) || obs - seen > CLOCK_SKEW_TOLERANCE_S * 1000) return { reason: "OBSERVED_AT_INCOHERENT" };
    }
  }
  if (age + lag > MEASURED_MAX_AGE_S) return { reason: "MEASURED_STALE" };
  return { courseDeg: deg, source: x.course_source };
}

// État de navigation d'un drone (live). `samples` = historique horodaté de ce même drone.
export function buildNavState(d, { nowMs = Date.now(), samples = null, freshness = null } = {}) {
  const x = d || {};
  const positionValid = isValidLatLng(x.lat, x.lng) && x.global_position_valid !== false;
  const fr = freshness || freshnessOf(x.last_seen, nowMs);
  const live = fr === FRESHNESS.FRESH || fr === FRESHNESS.REPLAY;
  const gps = gpsStateOf(x);
  const headingDeg = live ? normalizeDeg(x.heading) : null;
  const speedMps = live && isNum(x.speed) && x.speed >= 0 ? x.speed : null;
  const stationary = speedMps != null && speedMps < COURSE_RULES.stationarySpeedMps;

  let courseDeg = null, courseSource = COURSE_SOURCE.UNKNOWN, courseReason = null, measuredSource = null, measuredReason = null, courseQuality = null;
  if (!positionValid) courseReason = "POSITION_INVALID";
  else if (!live) courseReason = "STALE";
  else if (stationary) courseReason = "STATIONARY";
  else {
    const m = measuredCourse(x, { nowMs, replay: fr === FRESHNESS.REPLAY, positionValid, gps, speedMps });
    if (m.courseDeg != null) { courseDeg = m.courseDeg; courseSource = COURSE_SOURCE.MEASURED; measuredSource = m.source; courseQuality = COURSE_QUALITY.GPS_FIX; }
    else {
      measuredReason = m.reason;
      // Repli : course DÉRIVÉE uniquement si ses propres conditions sont prouvées ; jamais en NO_FIX.
      const r = gps === GPS.NO_FIX ? { courseDeg: null, reason: "GPS_NO_FIX" }
        : samples ? deriveCourse(samples, { nowMs: fr === FRESHNESS.REPLAY ? null : nowMs }) : { courseDeg: null, reason: "NO_SOURCE" };
      if (r.courseDeg != null) {
        courseDeg = r.courseDeg; courseSource = COURSE_SOURCE.DERIVED;
        courseQuality = gps === GPS.FIX && r.quality === COURSE_QUALITY.GPS_FIX ? COURSE_QUALITY.GPS_FIX : COURSE_QUALITY.GPS_UNDOCUMENTED;
      } else courseReason = r.reason;
    }
  }

  return {
    droneId: x.drone_id ?? null,
    lat: positionValid ? x.lat : null, lng: positionValid ? x.lng : null,
    positionValid, gps, freshness: fr,
    headingDeg, headingValid: headingDeg != null, headingSource: headingDeg != null ? "PIXHAWK" : null,
    speedMps, stationary,
    courseDeg, courseValid: courseDeg != null, courseSource, courseQuality, courseReason, measuredSource, measuredReason,
  };
}

// Libellé opérateur de la provenance du déplacement (cartes, relecture).
export function courseLabel(nav) {
  if (!nav || !nav.courseValid) return null;
  if (nav.courseSource === COURSE_SOURCE.MEASURED) return `mesuré ${nav.measuredSource}`;
  return nav.courseQuality === COURSE_QUALITY.GPS_FIX ? "dérivé GPS" : "dérivé GPS · qualité GPS non documentée";
}

// Relecture : cap historique UNIQUEMENT s'il a été enregistré (jamais fabriqué depuis la trajectoire) ;
// course dérivable depuis les positions historiques précédentes.
export function buildReplayNav(points, idx) {
  const p = (points || [])[idx];
  if (!p) return buildNavState(null, { freshness: FRESHNESS.REPLAY });
  const prev = points.slice(Math.max(0, idx - 11), idx + 1);
  return buildNavState({ ...p, drone_id: p.drone_id ?? "replay" }, { freshness: FRESHNESS.REPLAY, samples: prev });
}

// Vue cartographique SUIVRE : centre + rotation. Cap inconnu -> nord en haut (jamais une ancienne
// orientation). Position invalide -> recentrage suspendu.
export const FOLLOW_NOTICE = Object.freeze({ HEADING_UNAVAILABLE: "CAP INDISPONIBLE", POSITION_INVALID: "POSITION INVALIDE — SUIVI SUSPENDU" });
export function followView(follow, nav) {
  if (!follow) return { recenter: false, bearingDeg: 0, notice: null };
  if (!nav || !nav.positionValid) return { recenter: false, bearingDeg: 0, notice: FOLLOW_NOTICE.POSITION_INVALID };
  if (!nav.headingValid) return { recenter: true, bearingDeg: 0, notice: FOLLOW_NOTICE.HEADING_UNAVAILABLE };
  return { recenter: true, bearingDeg: nav.headingDeg, notice: null };
}

// Historique d'échantillons par drone (immuable) : ajout seulement si l'horodatage avance ; un
// horodatage qui recule réinitialise l'historique (jamais de direction sur données incohérentes).
// MAP.NAV.2H : NO_FIX / position invalide / coordonnées absentes -> historique réinitialisé.
export function appendSample(history, d, max = 12) {
  const s = toSample(d);
  const h = history || [];
  if (!s || s.gpsClass === GPS.NO_FIX) return h.length ? [] : h;
  const last = h[h.length - 1];
  if (last && s.t === last.t) return h;
  if (last && s.t < last.t) return [s];
  return [...h, s].slice(-max);
}
